/* eslint-disable react/require-default-props */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react'
import { Form, Input } from 'antd'
import Krpano from 'utils/krpano'

import Spinner from 'components/atoms/Spinner'

import QuillEditorV2 from '../ModalAlertSetting/QuillEditorV2'

interface BCPMemoSettingProps {
  memo?: string | undefined
  memoName?: string
  onMemoNameChange?: (name: string) => void
  /** Called whenever the editor HTML changes — used to pass content up to parent for submit */
  onMemoContentChange?: (content: string) => void
  setDisableWhenMemoNotCompleted?: Dispatch<SetStateAction<boolean>>
}

export default function BCPMemoSetting({
  memo,
  memoName,
  onMemoNameChange,
  onMemoContentChange,
  setDisableWhenMemoNotCompleted
}: BCPMemoSettingProps) {
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [quillContent, setQuillContent] = useState<string>('')
  const [form] = Form.useForm()

  const validateForm = useCallback(
    (content?: string) => {
      const nameContent = form.getFieldValue('memoName')
      const isMemoEmpty = Krpano.IsMemoEmpty(content || '')
      const nameValid =
        nameContent?.trim()?.length >= 1 && nameContent?.trim()?.length <= 100
      const isFormValid = Boolean(memoName) && nameValid && !isMemoEmpty

      if (setDisableWhenMemoNotCompleted) {
        setDisableWhenMemoNotCompleted(!isFormValid)
      }

      return isFormValid
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [form, memoName, setDisableWhenMemoNotCompleted]
  )

  /** Called by QuillEditorV2 whenever content changes; drives parent state + validation */
  const handleQuillChange = useCallback(
    (content: string) => {
      setQuillContent(content)
      onMemoContentChange?.(content)
      validateForm(content)
    },
    [validateForm, onMemoContentChange]
  )

  const handleFetchContent = useCallback(async () => {
    if (memo) {
      setIsLoadingContent(true)
      const textContent = await Krpano.FetchMemoContent(memo)
      setQuillContent(textContent)
      onMemoContentChange?.(textContent)
      setIsLoadingContent(false)
      validateForm(textContent)
    } else {
      setQuillContent('')
      onMemoContentChange?.('')
    }
  }, [memo, validateForm, onMemoContentChange])

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    form.setFieldsValue({ memoName: val })
    if (onMemoNameChange) {
      onMemoNameChange(val)
    }
    validateForm(quillContent)
  }

  useEffect(() => {
    handleFetchContent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memo])

  useEffect(() => {
    if (memoName) {
      form.setFieldsValue({ memoName })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoName])

  return (
    <Spinner spinning={isLoadingContent}>
      <div className="pt-[10px] px-[158px] mb-2">
        <Form
          component={false}
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          {memoName !== undefined && (
            <div className="relative flex items-start my-1">
              <span className="whitespace-nowrap text-sm font-bold text-gray-700 mr-2 w-20 absolute -left-[10rem] top-2 flex gap-2 items-center">
                メモ名
                <span className="text-red-500 text-xs whitespace-nowrap">
                  ※必須
                </span>
              </span>
              <Form.Item
                name="memoName"
                className="mb-0 flex-1 !w-1/2 !min-h-[72px]"
                rules={[
                  { required: true, message: 'メモ名を入力してください' },
                  {
                    min: 1,
                    max: 100,
                    message: '1〜100文字で入力してください'
                  }
                ]}
                initialValue={memoName}
              >
                <Input
                  placeholder="メモの名前を入力してください"
                  value={memoName}
                  onChange={handleNameChange}
                  className="w-full h-[38px]"
                />
              </Form.Item>
            </div>
          )}
          <Form.Item>
            <div className="flex flex-col">
              <QuillEditorV2
                value={quillContent}
                onChange={handleQuillChange}
                height={350}
              />
            </div>
          </Form.Item>
        </Form>
      </div>
    </Spinner>
  )
}
